import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getUsuarios, getUsuarioById, postUsuario, changePasswordUsuario } from "@/services/usuario";
import { Input } from "@/components/ui/inputForm";
import { Button as AntdButton, message, Table, Switch, Space, ConfigProvider, theme, Select, Modal, Form, Input as AntdInput } from "antd";
import { EditOutlined, PlusOutlined, LockOutlined } from '@ant-design/icons';
import { usePermissions } from "@/hooks/usePermissions";
import { isPerfilMaster } from "@/utils/perfis";
import { errorHandler } from "@/utils/errorhandler";

export default function ConsultaUsuario() {
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [changePwdModal, setChangePwdModal] = useState<{ open: boolean; userId: string | null }>({ open: false, userId: null });
  const [changePwdLoading, setChangePwdLoading] = useState(false);
  const [changePwdForm] = Form.useForm();
  const navigate = useNavigate();
  const { canInsert, canEdit, isMaster } = usePermissions();

  // Usuários com perfil MASTER só são editados por outro MASTER.
  const podeEditarUsuario = (record: any) =>
    canEdit(7) && (isMaster || !isPerfilMaster(record?.perfil ?? record?.perfilNome));

  useEffect(() => {
    handleGetUsuarios();
  }, []);
  
  const handleGetUsuarios = async () => {
    const { data } = await getUsuarios();
    setUsuarios(
      data.map((u: any) => ({
        ...u,
        ativo: u.ativo ?? true,
        perfilNome: u.perfil?.nome || "-",
        unidadesStr: Array.isArray(u.unidades) && u.unidades.length > 0
          ? u.unidades.map((uni: any) => uni.nome).join('; ')
          : "-"
      }))
    );
  };

  const handleAdd = useCallback(() => {
    navigate("/usuarios/cadastro");
  }, [navigate]);

  const handleEdit = (id: string) => {
    navigate(`/usuarios/${id}`);
  };

  const handleOpenChangePwd = (id: string) => {
    changePwdForm.resetFields();
    setChangePwdModal({ open: true, userId: id });
  };

  const handleChangePwd = async (values: { password: string }) => {
    try {
      setChangePwdLoading(true);
      await changePasswordUsuario(changePwdModal.userId!, values.password);
      message.success("Senha alterada com sucesso!");
      setChangePwdModal({ open: false, userId: null });
    } catch {
      message.error("Erro ao alterar senha.");
    } finally {
      setChangePwdLoading(false);
    }
  };

  const toggleAtivo = async (id: string) => {
    try {
      const { data: usuario } = await getUsuarioById(id);
      const updatedUsuario = { ...usuario, ativo: !usuario.ativo };
      const response = await postUsuario(updatedUsuario);
      
      if (response.status === 200 || response.status === 201) {
        setUsuarios((prev) =>
          prev.map((u) => (u.id === id ? { ...u, ativo: !u.ativo } : u))
        );
        message.success(`Usuário ${!usuario.ativo ? 'ativado' : 'inativado'} com sucesso!`);
      } else {
        message.error("Falha ao atualizar o status.");
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const columns = [
    { title: "Nome", dataIndex: "nome", key: "nome" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Perfil", dataIndex: "perfilNome", key: "perfilNome" },
    { title: "Unidades", dataIndex: "unidadesStr", key: "unidadesStr" },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (value: boolean, record: any) => (
        <Switch
          checked={!!value}
          disabled={!podeEditarUsuario(record)}
          onChange={() => toggleAtivo(record.id)}
          checkedChildren="Ativo"
          unCheckedChildren="Inativo"
        />
      ),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_: any, record: any) => (
        <Space size="middle">
          {podeEditarUsuario(record) && (
            <AntdButton type="text" icon={<EditOutlined />} onClick={() => handleEdit(record.id)} />
          )}
          {podeEditarUsuario(record) && (
            <AntdButton type="text" icon={<LockOutlined />} title="Alterar senha" onClick={() => handleOpenChangePwd(record.id)} />
          )}
        </Space>
      ),
    },
  ];

  const filteredUsuarios = usuarios.filter((u) => {
    const matchesSearch = u.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "todos" ||
      (statusFilter === "ativos" && u.ativo) ||
      (statusFilter === "inativos" && !u.ativo);
    return matchesSearch && matchesStatus;
  });

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Consulta de Usuários</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-end mb-8">
          <Input
            type="text"
            placeholder="Pesquisar por nome"
            className="w-80"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={[
              { value: "todos", label: "Todos" },
              { value: "ativos", label: "Ativos" },
              { value: "inativos", label: "Inativos" },
            ]}
          />
          {canInsert(7) && (
            <AntdButton className="flex gap-2" onClick={handleAdd}>
              Novo
              <PlusOutlined />
            </AntdButton>
          )}
        </div>
        <div className="grid">
          <div>
            <Table columns={columns} dataSource={filteredUsuarios} rowKey="id" />
          </div>
        </div>
        <Modal
          open={changePwdModal.open}
          title="Alterar Senha"
          okText="Confirmar"
          cancelText="Cancelar"
          okButtonProps={{ autoFocus: true, htmlType: 'submit', loading: changePwdLoading }}
          onCancel={() => setChangePwdModal({ open: false, userId: null })}
          destroyOnClose
          modalRender={(dom) => (
            <Form
              layout="vertical"
              form={changePwdForm}
              name="form_change_password"
              onFinish={handleChangePwd}
            >
              {dom}
            </Form>
          )}
        >
          <Form.Item
            name="password"
            label="Nova Senha"
            rules={[{ required: true, message: "Informe a nova senha" }, { min: 6, message: "Mínimo 6 caracteres" }]}
          >
            <AntdInput.Password placeholder="Nova senha" />
          </Form.Item>
        </Modal>
      </ConfigProvider>
    </>
  );
}
